import { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

interface CalendarPickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: string;
}

export default function CalendarPicker({
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
}: CalendarPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value);
    return new Date();
  });

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return placeholder;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const selectedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const dateStr = selectedDate.toISOString().split('T')[0];

    if (minDate && dateStr < minDate) return;

    onChange(dateStr);
    setIsOpen(false);
  };

  const isDateDisabled = (day: number) => {
    if (!minDate) return false;
    const dateStr = new Date(viewDate.getFullYear(), viewDate.getMonth(), day)
      .toISOString()
      .split('T')[0];
    return dateStr < minDate;
  };

  const isDateSelected = (day: number) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewDate.getMonth() &&
      selectedDate.getFullYear() === viewDate.getFullYear()
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === viewDate.getMonth() &&
      today.getFullYear() === viewDate.getFullYear()
    );
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(viewDate);

  const monthName = viewDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(<div key={`empty-${i}`} className="h-9" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(
      <button
        key={day}
        type="button"
        onClick={() => handleDateClick(day)}
        disabled={isDateDisabled(day)}
        className={`h-9 w-full rounded-lg text-[13px] font-medium transition-colors ${
          isDateSelected(day)
            ? 'bg-[#40E0D0] text-[#0A0A0A]'
            : isToday(day)
            ? 'bg-[#40E0D0]/20 text-[#40E0D0]'
            : isDateDisabled(day)
            ? 'text-[#606060] cursor-not-allowed'
            : 'text-[#F5F5F5] hover:bg-[#282828]'
        }`}
      >
        {day}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[13px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50 hover:bg-[#151515] transition-colors flex items-center justify-between"
      >
        <span className={value ? 'text-[#F5F5F5]' : 'text-[#808080]'}>
          {formatDisplayDate(value)}
        </span>
        <CalendarIcon className="w-4 h-4 text-[#808080]" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 mt-2 p-4 bg-[#1A1A1A] border border-[#282828] rounded-xl shadow-xl min-w-[320px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-2 hover:bg-[#282828] rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-[#F5F5F5]" />
              </button>
              <div className="text-[14px] font-semibold text-[#F5F5F5]">
                {monthName}
              </div>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-2 hover:bg-[#282828] rounded-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-[#F5F5F5]" />
              </button>
            </div>

            {/* Days of week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div
                  key={day}
                  className="h-9 flex items-center justify-center text-[11px] font-medium text-[#808080]"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">{days}</div>
          </div>
        </>
      )}
    </div>
  );
}
